import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, fmtLbs, daysDiff, stockBadge } from '../components/helpers';

export default function Dashboard({ navigate }) {
  const [raw, setRaw] = useState([]);
  const [finished, setFinished] = useState([]);
  const [po, setPo] = useState([]);
  const [ar, setAr] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [r, f, p, a] = await Promise.all([
        supabase.from('raw_materials').select('*'),
        supabase.from('finished_goods').select('*'),
        supabase.from('purchase_orders').select('*'),
        supabase.from('customer_orders').select('*'),
      ]);
      setRaw(r.data || []);
      setFinished(f.data || []);
      setPo(p.data || []);
      setAr(a.data || []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{color:'var(--muted)',padding:40}}>Loading dashboard...</div>;

  const now = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const lowRaw = raw.filter(r => Number(r.on_hand) <= Number(r.reorder_at));
  const lowFG = finished.filter(f => Number(f.on_hand) <= Number(f.reorder_at));
  const totalAlerts = lowRaw.length + lowFG.length;
  const totalResin = raw.reduce((s, r) => s + Number(r.on_hand), 0);
  const totalFGUnits = finished.reduce((s, f) => s + Number(f.on_hand), 0);
  const openAR = ar.filter(o => o.status !== 'Paid').reduce((s, o) => s + Number(o.amount), 0);
  const overdueAR = ar.filter(o => o.status !== 'Paid' && o.due_date && daysDiff(o.due_date) > 0);
  const openPOs = po.filter(p => p.status !== 'Received').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">{now}</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Stock Alerts</div>
          <div className="stat-val">{totalAlerts}</div>
          <div className="stat-sub">items below reorder point</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Resin on Hand</div>
          <div className="stat-val">{totalResin.toLocaleString()}</div>
          <div className="stat-sub">lbs across {raw.length} materials</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Finished Goods</div>
          <div className="stat-val">{totalFGUnits.toLocaleString()}</div>
          <div className="stat-sub">units across {finished.length} SKUs</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">AR Outstanding</div>
          <div className="stat-val">{fmt$(openAR)}</div>
          <div className="stat-sub">{overdueAR.length} overdue · {openPOs} open POs</div>
        </div>
      </div>

      <div className="tbl-section">
        <div className="tbl-header"><span className="tbl-title">Low Stock Items</span></div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Item</th><th>Type</th><th>On Hand</th><th>Reorder At</th><th>Status</th></tr></thead>
            <tbody>
              {lowRaw.length === 0 && lowFG.length === 0
                ? <tr className="empty-row"><td colSpan={5}>All stock levels are healthy.</td></tr>
                : <>
                    {lowRaw.map(r => <tr key={r.id}><td><strong>{r.name}</strong></td><td><span className="badge badge-gray">Raw Material</span></td><td className="mono">{fmtLbs(r.on_hand)}</td><td className="mono">{fmtLbs(r.reorder_at)}</td><td>{stockBadge(r.on_hand, r.reorder_at)}</td></tr>)}
                    {lowFG.map(f => <tr key={f.id}><td><strong>{f.sku} – {f.name}</strong></td><td><span className="badge badge-gray">Finished Good</span></td><td className="mono">{Number(f.on_hand).toLocaleString()} units</td><td className="mono">{Number(f.reorder_at).toLocaleString()} units</td><td>{stockBadge(f.on_hand, f.reorder_at)}</td></tr>)}
                  </>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div className="tbl-section">
        <div className="tbl-header">
          <span className="tbl-title">Overdue Customer Payments</span>
          <button className="btn btn-sm" onClick={() => navigate('ar')}>View all →</button>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Customer</th><th>Order #</th><th>Amount</th><th>Due Date</th><th>Days Overdue</th></tr></thead>
            <tbody>
              {overdueAR.length === 0
                ? <tr className="empty-row"><td colSpan={5}>No overdue payments.</td></tr>
                : overdueAR.map(o => <tr key={o.id}><td>{o.customer}</td><td className="mono">{o.order_number}</td><td className="mono">{fmt$(o.amount)}</td><td>{o.due_date}</td><td className="text-danger">{daysDiff(o.due_date)} days</td></tr>)
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
